import { useQueryClient } from '@tanstack/react-query';

import type { ProductListPage } from '@nucleus-commerce/api-client/admin';

import { productKeys } from './product-keys';

export interface AdjacentProducts {
  previousId: string | null;
  nextId: string | null;
}

const isPage = (data: unknown): data is ProductListPage =>
  typeof data === 'object' && data !== null && Array.isArray((data as ProductListPage).items);

/**
 * Previous and next from the products list the person most recently loaded, as it sits in the
 * cache; null when that list does not hold this product or holds nothing else. No request: it
 * steps through the page the person came from, not the whole catalog.
 */
export function useAdjacentProducts(productId: string): AdjacentProducts | null {
  const queryClient = useQueryClient();
  const latest = queryClient
    .getQueryCache()
    .findAll({ queryKey: productKeys.lists() })
    .filter((query) => isPage(query.state.data))
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)[0];
  const items = isPage(latest?.state.data) ? latest.state.data.items : [];
  const index = items.findIndex((item) => item.id === productId);
  if (index < 0 || items.length < 2) return null;
  return {
    previousId: items[index - 1]?.id ?? null,
    nextId: items[index + 1]?.id ?? null,
  };
}
